import axios from "axios";
import { message } from "antd";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

// 创建axios实例
const api = axios.create({
    baseURL: API_BASE,
    timeout: 30000,
    headers: { "Content-Type": "application/json" }
});

// 请求拦截器
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("token");
        if (token) {
            config.headers.Authorization = "Bearer " + token;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 响应拦截器 - 优化错误处理
api.interceptors.response.use(
    (response) => response.data,
    (error) => {
        // 处理不同类型的错误
        if (error.response) {
            // 服务器返回了错误状态码
            const { status, data } = error.response;
            
            switch (status) {
                case 401:
                    if (/\/auth\/(login|register)$/.test(error.config?.url || "")) break;
                    // 未授权或被踢下线 - 清除本地存储并跳转到登录页
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    // 如果是被踢下线，显示提示
                    if (data && data.error && data.error.includes("其他设备登录")) {
                        message.error("您的账号在其他设备登录，已被迫下线");
                    }
                    if (window.location.pathname !== "/login") window.location.replace("/login");
                    break;
                case 403:
                    // 禁止访问
                    console.error("权限不足:", data?.error || "无法访问该资源");
                    break;
                case 404:
                    // 资源不存在
                    console.error("资源不存在:", data?.error || "请求的资源不存在");
                    break;
                case 500:
                    // 服务器错误
                    console.error("服务器错误:", data?.error || "服务器内部错误");
                    break;
                default:
                    console.error("请求失败:", data?.error || "请求失败");
            }
            
            return Promise.reject({ ...(data && typeof data === "object" ? data : {}), error: data?.error || "请求失败", status });
        } else if (error.request) {
            // 请求已发出但没有收到响应
            console.error("网络错误: 无法连接到服务器");
            return Promise.reject({ error: "网络连接失败，请检查网络设置", status: 0 });
        } else {
            // 设置请求时发生错误
            console.error("请求配置错误:", error.message);
            return Promise.reject({ error: "请求配置错误" });
        }
    }
);

// 带重试的请求函数
const requestWithRetry = async (requestFn, maxRetries = 2) => {
    let lastError;
    
    for (let i = 0; i <= maxRetries; i++) {
        try {
            return await requestFn();
        } catch (error) {
            lastError = error;
            
            // 如果是401错误，不再重试
            if (![0, 502, 503, 504].includes(error.status)) {
                throw error;
            }
            
            // 如果还有重试次数，等待后重试
            if (i < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
                console.log(`重试请求 (${i + 1}/${maxRetries})...`);
            }
        }
    }
    
    throw lastError;
};

// 认证API
export const login = (data) => api.post("/auth/login", data);
export const register = (data) => api.post("/auth/register", data);
export const applyLandlord = () => api.post("/auth/apply-landlord");

// 用户API
export const getProfile = () => api.get("/users/profile");
export const updateProfile = (data) => api.put("/users/profile", data);
export const changePassword = (data) => api.put("/users/password", data);
export const requestAccountDeletion = (data) => api.post("/users/deletion-request", data);
export const getMembershipProducts = () => api.get("/membership/products");
export const getMyUpgradeRequest = () => api.get("/membership/my-upgrade-request");
export const submitUpgradeRequest = (data) => api.post("/membership/upgrade-requests", data);

// 楼栋API
export const getBuildings = () => api.get("/buildings");
export const createBuilding = (data) => api.post("/buildings", data);
export const updateBuilding = (id, data) => api.put("/buildings/" + id, data);
export const deleteBuilding = (id) => api.delete("/buildings/" + id);

// 房间API
export const getRooms = (params) => api.get("/rooms", { params });
export const getRoom = (id) => api.get("/rooms/" + id);
export const createRoom = (data) => api.post("/rooms", data);
export const batchCreateRooms = (data) => api.post("/rooms/batch-create", data);
export const updateRoom = (id, data) => api.put("/rooms/" + id, data);
export const deleteRoom = (id) => api.delete("/rooms/" + id);
export const listRoom = (id) => api.post("/rooms/" + id + "/list");
export const unlistRoom = (id) => api.post("/rooms/" + id + "/unlist");
export const batchListRooms = (room_ids, commission) => api.post("/rooms/batch-list", { room_ids, commission });
export const batchUnlistRooms = (room_ids) => api.post("/rooms/batch-unlist", { room_ids });
export const updateRoomStatus = (id, data) => api.put("/rooms/" + id + "/status", data);
export const getMyRooms = () => api.get("/my-rooms");

// 公共池API
export const getPublicRooms = (params) => api.get("/public", { params });
export const getPublicRoom = (id) => api.get("/public/" + id);
export const getMapRooms = (params) => api.get("/public/map", { params });

// 合同API
export const getContracts = (roomId) => api.get("/contracts/room/" + roomId);
export const getContract = (id) => api.get("/contracts/" + id);
export const createContract = (data) => api.post("/contracts", data);
export const updateContract = (id, data) => api.put("/contracts/" + id, data);
export const deleteContract = (id) => api.delete("/contracts/" + id);

// 消息API - 带重试机制
export const getConversations = () => requestWithRetry(() => api.get("/messages/conversations"));
export const getCustomerService = () => requestWithRetry(() => api.get("/messages/customer-service"));
export const getConversation = (userId, params) => requestWithRetry(() => api.get("/messages/conversation/" + userId, { params }));
export const sendMessage = (data) => api.post("/messages", data);
export const getUnreadCount = () => requestWithRetry(() => api.get("/messages/unread-count"));
export const deleteConversation = (userId) => api.delete("/messages/conversation/" + userId);
export const deleteConversations = (userIds) => api.delete("/messages/conversations", { data: { user_ids: userIds } });

// 入住工单API
export const getWorkOrders = (params) => requestWithRetry(() => api.get("/work-orders", { params }));
export const getWorkOrder = (id) => api.get("/work-orders/" + id);
export const createWorkOrder = (data) => api.post("/work-orders", data);
export const uploadWorkOrderImages = (id, formData) => api.post("/work-orders/" + id + "/images", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const replyWorkOrder = (id, content) => api.post("/work-orders/" + id + "/replies", { content });
export const updateWorkOrderStatus = (id, status) => api.put("/work-orders/" + id + "/status", { status });
export const markWorkOrderRead = (id) => api.put("/work-orders/" + id + "/read");
export const getWorkOrderUnreadCount = () => requestWithRetry(() => api.get("/work-orders/unread-count"));
export const deleteWorkOrder = (id) => api.delete("/work-orders/" + id);

// 文件上传API
export const uploadImage = (formData) => api.post("/upload/image", formData, { 
    headers: { "Content-Type": "multipart/form-data" } 
});
export const uploadImages = (formData) => api.post("/upload/images", formData, { 
    headers: { "Content-Type": "multipart/form-data" } 
});
export const uploadVideo = (formData) => api.post("/upload/video", formData, { 
    headers: { "Content-Type": "multipart/form-data" } 
});
export const getRoomMedia = (roomId) => api.get("/upload/room/" + roomId);
export const deleteMedia = (id) => api.delete("/upload/" + id);

// 统计API
export const getStatistics = () => api.get("/statistics");
export const exportData = (type) => api.get("/statistics/export", { 
    params: { type }, 
    responseType: "blob" 
});

// 子账号API
export const getSubAccounts = () => api.get("/sub-accounts");
export const inviteSubAccount = (data) => api.post("/sub-accounts", data);
export const updateSubAccountPermissions = (id, data) => api.put("/sub-accounts/" + id + "/permissions", data);
export const deleteSubAccount = (id) => api.delete("/sub-accounts/" + id);

// 管理员API
export const getAdminUsers = (params) => api.get("/admin/users", { params });
export const updateUserStatus = (id, status) => api.put("/admin/users/" + id + "/status", { status });
export const updateUserRole = (id, role) => api.put("/admin/users/" + id + "/role", { role });
export const getAdminRegions = () => api.get("/admin/regions");
export const createRegion = (name) => api.post("/admin/regions", { name });
export const updateRegion = (id, name) => api.put("/admin/regions/" + id, { name });
export const deleteRegion = (id) => api.delete("/admin/regions/" + id);
export const getAdminStreets = (params) => api.get("/admin/streets", { params });
export const createStreet = (data) => api.post("/admin/streets", data);
export const deleteStreet = (id) => api.delete("/admin/streets/" + id);
export const getAdminStatistics = () => api.get("/admin/statistics");
export const getPageImages = () => api.get("/site/page-images");
export const uploadAdminPageImage = (slot, formData) => api.post("/admin/page-images/" + slot, formData, { headers: { "Content-Type": "multipart/form-data" } });
export const deleteAdminPageImage = (slot) => api.delete("/admin/page-images/" + slot);
export const getAdminMembershipProducts = () => api.get("/admin/membership-products");
export const createMembershipProduct = (data) => api.post("/admin/membership-products", data);
export const updateMembershipProduct = (id, data) => api.put("/admin/membership-products/" + id, data);
export const updateMembershipProductStatus = (id, status) => api.put("/admin/membership-products/" + id + "/status", { status });
export const getAdminUpgradeRequests = () => api.get("/admin/upgrade-requests");
export const reviewUpgradeRequest = (id, data) => api.put("/admin/upgrade-requests/" + id + "/review", data);

// 区域和街道（公开）
export const getRegions = () => api.get("/regions");
export const getStreets = (params) => api.get("/streets", { params });

// 健康检查
export const healthCheck = () => api.get("/health");

export default api;

